import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit
} from '@angular/core';
import { BehaviorSubject, first, Subject, take, takeUntil } from 'rxjs';
import { LgvLoanApp } from '../../../data-models/lgv-loan-application.model';
import { LoanAppPageService } from '../../../core/services/page-services/loan-app-page.service';
import { LgvLoanApplicationService } from '../../../core/services/lgv-loan-application.service';
import { LgvAuthMgrService } from '../../../core/services/lgv-auth-mgr.service';
import { LgvTSHelper, LgvUserService } from '../../../core/core.module';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router, NavigationEnd, Navigation } from '@angular/router';

@Component({
    selector: 'lgv-loan-app-details',
    templateUrl: './loan-app-details.component.html',
    styleUrls: ['./loan-app-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoanAppDetailsComponent implements OnInit {
    _timeline: LgvLoanApp.Timeline = null;
    timeline$ = new BehaviorSubject(this._timeline);
    onDestroy$ = new Subject();

    get isClient(): boolean {
        return this.lgvAuthMgrSvc.IsClient;
    }
    get isInternalUser(): boolean {
        return !this.isClient;
    }
    get loanAppId(): string {
        return this.loanAppPageSvc.loanAppId$.value;
    }

    selectedLoanApp = this.loanAppPageSvc.selectedLoanApplication$.value;
    appID = this.isClient
        ? this.loanAppPageSvc.selectedLoanApplication$.value.id
        : this.loanAppId;

    constructor(
        public loanAppPageSvc: LoanAppPageService,
        private matDialog: MatDialog,
        public lgvUserSvc: LgvUserService,
        public lgvAuthMgrSvc: LgvAuthMgrService,
        public lgvLoanAppService: LgvLoanApplicationService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.loanAppPageSvc.selectedLoanApplication$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                x.loanTimeline.loanStatuses = x.loanTimeline.loanStatuses.sort(
                    x => x.displayOrder
                );

                this.timeline$.next(x.loanTimeline);
            });
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    isActive(timeLineItem: LgvLoanApp.LoanStatus) {
        return (
            timeLineItem &&
            this.timeline$.value &&
            this.timeline$.value?.activeStatus?.code === timeLineItem.code
        );
    }

    isLastItem(timeLineItem: LgvLoanApp.LoanStatus) {
        return (
            timeLineItem &&
            this.timeline$.value &&
            this.timeline$.value?.loanStatuses.length > 0 &&
            this.timeline$.value?.loanStatuses[
                this.timeline$.value?.loanStatuses.length - 1
            ].code === timeLineItem.code
        );
    }

    goToShortAppPage() {
        if (this.lgvUserSvc.isPreLicensedAgent) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }
        this.loanAppPageSvc.openEconsentDialog(
            `/admin/short-app/${this.loanAppId}/-1/-1`,
            this.appID,
            this.isClient
        );
    }

    goToLoanAppPage() {
        if (this.lgvUserSvc.isPreLicensedAgent) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }
        let link = '';
        if (!this.isClient) {
            link = `/admin/admin-view-loan-app/appId/${this.loanAppId}/-1/-1`;
        } else {
            link = `/client/loan-applications/${this.loanAppPageSvc.selectedLoanApplication$.value.id}/-1/-1`;
        }
        this.loanAppPageSvc.openEconsentDialog(link, this.appID, this.isClient);
    }

    getToolTipForStatus(timelineItem: LgvLoanApp.LoanStatus): string {
        let rtnVal = '';

        if (timelineItem?.statusDetails) {
            for (let sd of timelineItem.statusDetails) {
                rtnVal += `<li>${sd.name} (${LgvTSHelper.shortDateString(
                    sd.date
                )})</li>`;
            }
        }

        if (rtnVal.length > 0) {
            rtnVal = '<b>Processing Details</b><br/><br/>' + rtnVal;
        } else {
            rtnVal = 'No details for this item.';
        }

        return rtnVal;
    }

    showToolTipModal(timelineItem: LgvLoanApp.LoanStatus) {
        let innerText = this.getToolTipForStatus(timelineItem);
        this.matDialog.open(ConfirmationDialogComponent, {
            data: {
                // header: 'Borrower',
                Confirmation: innerText,
                woCancelButton: true,
                button: 'Close'
            }
        });
    }

    isLoanSubmitted() {
        const preApplicationStatus = this.timeline$.value.loanStatuses?.find(
            ls => ls.code == 'PreApplication'
        );
        const preQualStatus = this.timeline$.value.loanStatuses?.find(
            ls => ls.code == 'PreQualification'
        );
        const preApprovalStatus = this.timeline$.value.loanStatuses?.find(
            ls => ls.code == 'PreApproval'
        );
        return (
            preApprovalStatus?.isComplete &&
            preQualStatus?.isComplete &&
            preApplicationStatus?.isComplete
        );
    }

    onConfirmArchiveLoan() {
        if (this.lgvUserSvc.isPreLicensedAgent) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: {
                Confirmation: `Are you sure you want to archive this loan?`
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.archiveLoan();
                }
            });
    }

    archiveLoan() {
        this.selectedLoanApp.isDeleted = true;
        this.lgvLoanAppService
            .deleteThisLoanApp(this.loanAppId)
            .subscribe(_ => this.router.navigate([`/admin`]));
    }

    restoreLoan() {
        if (this.lgvUserSvc.isPreLicensedAgent) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }
        this.selectedLoanApp.isDeleted = false;
        this.lgvLoanAppService
            .restoreThisLoanApp(this.loanAppId)
            .subscribe(_ => {});
    }
}
